import { z } from 'zod';
import { ROLES } from './roles.js';
import { ALL_PERMISSION_CODES } from './permissions.js';
import { ALL_MODULE_CODES } from './modules.js';
import { MESSAGE_PROVIDERS, STAFF_TYPES, WATI_FIELD_SOURCES } from './types.js';
export const loginSchema = z.object({
    email: z.string().email('Invalid email'),
    password: z.string().min(1, 'Password is required'),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be at most 72 characters'),
});
export const setStaffPasswordSchema = z.object({
    new_password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be at most 72 characters'),
});
export const updateProfileSchema = z.object({
    first_name: z.string().min(1).max(100).optional(),
    last_name: z.string().min(1).max(100).optional(),
    phone: z.string().max(20).nullable().optional(),
    dob: z.string().date().nullable().optional(),
    gender: z.enum(['male', 'female', 'other']).nullable().optional(),
    marital_status: z.string().max(20).nullable().optional(),
    blood_group: z.string().max(10).nullable().optional(),
    emergency_contact: z.string().max(20).nullable().optional(),
    father_name: z.string().max(100).nullable().optional(),
    mother_name: z.string().max(100).nullable().optional(),
    spouse_name: z.string().max(100).nullable().optional(),
    address_line1: z.string().max(255).nullable().optional(),
    address_line2: z.string().max(255).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    postal_code: z.string().max(20).nullable().optional(),
    joining_date: z.string().date().nullable().optional(),
    physically_challenged: z.boolean().nullable().optional(),
});
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    sort: z.string().optional(),
    q: z.string().optional(),
});
// ---- Staff (users) ----
const roleCodeSchema = z.enum([
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.MANAGER,
    ROLES.TEAM_LEADER,
    ROLES.USER,
]);
const moduleCodeSchema = z.enum(ALL_MODULE_CODES);
const permissionCodeSchema = z.enum(ALL_PERMISSION_CODES);
export const createStaffSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(72),
    employee_code: z.string().min(1).max(50),
    first_name: z.string().min(1).max(100),
    last_name: z.string().min(1).max(100),
    phone: z.string().min(1, 'Phone is required').max(20),
    role_codes: z.array(roleCodeSchema).min(1, 'At least one role required'),
    department_id: z.string().uuid({ message: 'Department is required' }),
    designation_id: z.string().uuid({ message: 'Designation is required' }),
    // Defaulted rather than required so the existing create contract still holds
    // for callers predating the staff register export.
    staff_type: z.enum(STAFF_TYPES).default('permanent'),
    module_overrides: z
        .object({
        include: z.array(moduleCodeSchema).optional(),
        exclude: z.array(moduleCodeSchema).optional(),
    })
        .optional(),
    dob: z.string().date('Date of birth is required'),
    gender: z.enum(['male', 'female', 'other'], { errorMap: () => ({ message: 'Gender is required' }) }),
    marital_status: z.string().max(20).nullable().optional(),
    blood_group: z.string().max(10).nullable().optional(),
    emergency_contact: z.string().max(20).nullable().optional(),
    father_name: z.string().max(100).nullable().optional(),
    mother_name: z.string().max(100).nullable().optional(),
    spouse_name: z.string().max(100).nullable().optional(),
    physically_challenged: z.boolean().nullable().optional(),
    address_line1: z.string().min(1, 'Address line 1 is required').max(255),
    address_line2: z.string().max(255).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    postal_code: z.string().max(20).nullable().optional(),
    joining_date: z.string().date('Joining date is required'),
    work_start_time: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Shift start time is required (HH:MM)'),
    work_end_time: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Shift end time is required (HH:MM)'),
});
export const updateStaffSchema = z.object({
    email: z.string().email().optional(),
    employee_code: z.string().min(1).max(50).optional(),
    first_name: z.string().min(1).max(100).optional(),
    last_name: z.string().min(1).max(100).optional(),
    phone: z.string().max(20).nullable().optional(),
    department_id: z.string().uuid().nullable().optional(),
    designation_id: z.string().uuid().nullable().optional(),
    staff_type: z.enum(STAFF_TYPES).nullable().optional(),
    is_active: z.boolean().optional(),
    credit_start_date: z.string().date().nullable().optional(),
});
export const updateStaffProfileSchema = z.object({
    dob: z.string().date().nullable().optional(),
    gender: z.enum(['male', 'female', 'other']).nullable().optional(),
    marital_status: z.string().max(20).nullable().optional(),
    blood_group: z.string().max(10).nullable().optional(),
    emergency_contact: z.string().max(20).nullable().optional(),
    father_name: z.string().max(100).nullable().optional(),
    mother_name: z.string().max(100).nullable().optional(),
    spouse_name: z.string().max(100).nullable().optional(),
    physically_challenged: z.boolean().nullable().optional(),
    address_line1: z.string().max(255).nullable().optional(),
    address_line2: z.string().max(255).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    postal_code: z.string().max(20).nullable().optional(),
    joining_date: z.string().date().nullable().optional(),
    work_start_time: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Shift start time is required (HH:MM)').optional(),
    work_end_time: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Shift end time is required (HH:MM)').optional(),
});
export const listStaffQuerySchema = paginationQuerySchema.extend({
    role: roleCodeSchema.optional(),
    department_id: z.string().uuid().optional(),
    is_active: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export const assignRolesSchema = z.object({
    role_codes: z.array(roleCodeSchema).min(1, 'At least one role required'),
});
export const assignModulesSchema = z.object({
    add: z.array(moduleCodeSchema).optional(),
    remove: z.array(moduleCodeSchema).optional(),
});
export const setRolePermissionsSchema = z.object({
    permission_codes: z.array(permissionCodeSchema),
});
export const setRoleDefaultModulesSchema = z.object({
    module_codes: z.array(moduleCodeSchema),
});
// ---- Department / Designation ----
export const createDepartmentSchema = z.object({
    code: z.string().min(1).max(50),
    name: z.string().min(1).max(100),
    head_user_id: z.string().uuid().nullable().optional(),
});
export const updateDepartmentSchema = createDepartmentSchema.partial().extend({
    is_active: z.boolean().optional(),
});
export const createDesignationSchema = z.object({
    code: z.string().min(1).max(50),
    name: z.string().min(1).max(100),
    level: z.coerce.number().int().min(0).max(100).nullable().optional(),
});
export const updateDesignationSchema = createDesignationSchema.partial().extend({
    is_active: z.boolean().optional(),
});
// ── Attendance ────────────────────────────────────────────────────────────────
/**
 * Allowed values of attendance_punch_logs.geo_fence_status — kept in step with the
 * chk_punch_logs_geo_fence_status CHECK constraint (migration 185).
 */
export const geoFenceStatusEnum = z.enum(['inside', 'outside', 'unknown']);
export const punchSchema = z.object({
    punch_type: z.enum(['check_in', 'check_out']),
    punch_time: z.string().datetime().optional(),
    punch_source: z.enum(['face_scan', 'manual', 'app', 'kiosk', 'web']).default('manual'),
    face_verification_score: z.number().min(0).max(100).nullable().optional(),
    face_registration_id: z.string().uuid().nullable().optional(),
    is_verified: z.boolean().default(false),
    device_id: z.string().max(100).nullable().optional(),
    device_type: z.enum(['kiosk', 'mobile', 'web']).nullable().optional(),
    work_mode: z.enum(['office', 'remote', 'hybrid']).nullable().optional(),
    raw_image_url: z.string().url().nullable().optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
    location_data: z.record(z.unknown()).nullable().optional(),
    geo_fence_status: geoFenceStatusEnum.nullable().optional(),
});
export const updatePunchSchema = z.object({
    punch_type: z.enum(['check_in', 'check_out']).optional(),
    punch_time: z.string().optional(),
    work_mode: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
});
export const deletePunchSchema = z.object({
    reason: z.string().min(1, 'Reason is required').max(500),
});
export const regularizeSchema = z.object({
    reason: z.string().min(1).max(500),
    status: z.enum(['approved', 'rejected']),
});
export const requestRegularizationSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const listAttendanceQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(1000).default(20),
    // Return every matching row in one response (no LIMIT/OFFSET). Used by the
    // Attendance Report, which lists all employees for a single date.
    all: z.union([z.boolean(), z.literal('true'), z.literal('false')]).optional()
        .transform((v) => v === true || v === 'true'),
    user_id: z.string().uuid().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
    search: z.string().max(100).optional(),
    status: z.enum(['present', 'absent', 'late', 'half_day', 'holiday', 'leave', 'work_from_home']).optional(),
    regularization_status: z.enum(['none', 'requested', 'approved', 'rejected']).optional(),
});
// Which late days in a range are excused by approved leave. Always scoped to one
// employee and one date range — there is no all-employees variant.
export const lateExcusesQuerySchema = z.object({
    user_id: z.string().uuid(),
    date_from: z.string().date(),
    date_to: z.string().date(),
});
export const createCorrectionSchema = z.object({
    reason: z.string().min(10, 'Reason must be at least 10 characters').max(500),
    punch_log_id: z.string().uuid().optional(),
    requested_punch_type: z.enum(['check_in', 'check_out']).optional(),
    requested_punch_time: z.string().datetime({ offset: true }).optional(),
}).refine((v) => !(v.punch_log_id || v.requested_punch_type) || !!v.requested_punch_time, { message: 'requested_punch_time is required when punch_log_id or requested_punch_type is set', path: ['requested_punch_time'] }).refine((v) => !(v.punch_log_id && v.requested_punch_type), { message: 'punch_log_id and requested_punch_type are mutually exclusive', path: ['requested_punch_type'] });
export const reviewCorrectionSchema = z.object({
    status: z.enum(['approved', 'rejected']),
    admin_remarks: z.string().max(500).optional(),
});
export const listCorrectionsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z.enum(['requested', 'approved', 'rejected']).optional(),
    user_id: z.string().uuid().optional(),
    daily_record_id: z.string().uuid().optional(),
    // Filter by the IST date the request was submitted (requested_at), inclusive
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
    // Employee name / code search, applied on the server so pagination counts only matches
    q: z.string().trim().max(100).optional(),
});
// ── Leave Management ──────────────────────────────────────────────────────────
export const createLeaveTypeSchema = z.object({
    name: z.string().min(2).max(100),
    category: z.enum(['normal', 'earned', 'special']),
    is_paid: z.boolean(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#6B7280'),
    description: z.string().max(300).optional(),
    approval_method: z.enum(['self', 'senior', 'role_based', 'any_of_role', 'any_one']).default('role_based').optional(),
});
export const updateLeaveTypeSchema = createLeaveTypeSchema.partial().extend({
    is_active: z.boolean().optional(),
});
export const createLeaveRuleSchema = z.object({
    leave_type_id: z.string().uuid(),
    name: z.string().min(2).max(100),
    monthly_credit: z.number().min(0).max(31),
    paid_hours_per_day: z.number().min(0).max(24).default(8),
    allow_half_day: z.boolean().default(false),
    allow_carry_forward: z.boolean().default(false),
});
export const updateLeaveRuleSchema = createLeaveRuleSchema.omit({ leave_type_id: true }).partial().extend({
    is_active: z.boolean().optional(),
});
export const adjustLeaveBalanceSchema = z.object({
    leave_type_id: z.string().uuid(),
    balance_year: z.number().int().min(2000).max(2100),
    action: z.enum(['add', 'remove']),
    days: z.number().positive().max(60).multipleOf(0.5),
});
export const updateLeaveSettingsSchema = z.object({
    auto_cancel_leave_on_punch: z.boolean(),
});
// Dates on which no leave (any type, short leave included) may be applied
export const createLeaveBlockedDatesSchema = z.object({
    dates: z.array(z.string().date()).min(1).max(366),
    reason: z.string().trim().max(200).optional(),
});
export const listLeaveBlockedDatesQuerySchema = z.object({
    from: z.string().date().optional(),
    to: z.string().date().optional(),
});
export const setApprovalFlowSchema = z.array(z.object({
    step_order: z.number().int().positive(),
    approver_role: z.enum(['MANAGER', 'TEAM_LEADER', 'ADMIN', 'SUPER_ADMIN']),
    is_final: z.boolean(),
}));
export const setShortLeaveFlowSchema = z.array(z.object({
    step_order: z.number().int().positive(),
    approver_role: z.enum(['MANAGER', 'TEAM_LEADER', 'ADMIN', 'SUPER_ADMIN']),
    is_final: z.boolean(),
}));
export const setAnyOneApproversSchema = z.array(z.string().uuid()).min(1);
export const setShortLeaveAnyOneApproversSchema = z.array(z.string().uuid()).min(1);
export const createLeaveRequestSchema = z.object({
    leave_type_id: z.string().uuid(),
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    is_half_day: z.boolean().default(false),
    half_day_period: z.enum(['morning', 'afternoon']).optional(),
    reason: z.string().min(3).max(500),
    document_url: z.string().url().optional(),
}).refine((d) => d.end_date >= d.start_date, {
    message: 'End date must be on or after start date',
    path: ['end_date'],
});
export const updateLeaveRequestSchema = z.object({
    leave_type_id: z.string().uuid(),
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    is_half_day: z.boolean().default(false),
    half_day_period: z.enum(['morning', 'afternoon']).optional(),
    reason: z.string().min(3).max(500),
    document_url: z.string().url().optional(),
}).refine((d) => d.end_date >= d.start_date, {
    message: 'End date must be on or after start date',
    path: ['end_date'],
});
export const reviewLeaveRequestSchema = z.object({
    action: z.enum(['approved', 'rejected']),
    remarks: z.string().max(500).optional(),
});
export const createShortLeaveSchema = z.object({
    request_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    from_time: z.string().regex(/^\d{2}:\d{2}$/),
    to_time: z.string().regex(/^\d{2}:\d{2}$/),
    reason: z.string().min(3).max(500),
}).refine((d) => d.to_time > d.from_time, {
    message: 'End time must be after start time',
    path: ['to_time'],
});
export const updateShortLeaveSchema = z.object({
    request_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    from_time: z.string().regex(/^\d{2}:\d{2}$/),
    to_time: z.string().regex(/^\d{2}:\d{2}$/),
    reason: z.string().min(3).max(500),
}).refine((d) => d.to_time > d.from_time, {
    message: 'End time must be after start time',
    path: ['to_time'],
});
export const reviewShortLeaveSchema = z.object({
    action: z.enum(['approved', 'rejected']),
    rejection_reason: z.string().max(500).optional(),
});
export const listLeaveRequestsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
    leave_type_id: z.string().uuid().optional(),
    user_id: z.string().uuid().optional(),
    date_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    date_to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    search: z.string().optional(),
    // Default created_at (filing order); start_date = by leave date, newest first.
    sort: z.enum(['created_at', 'start_date']).optional(),
});
export const listShortLeavesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
    user_id: z.string().uuid().optional(),
    date_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    date_to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    search: z.string().optional(),
});
// ── Payroll ───────────────────────────────────────────────────────────────────
export const upsertFinancialDataSchema = z.object({
    per_hour_rate: z.coerce.number().min(0),
    salary_cycle_start_date: z.string().date().nullable().optional(),
    pan_number: z.string().max(10).nullable().optional(),
    aadhar_front_url: z.string().url().nullable().optional(),
    aadhar_back_url: z.string().url().nullable().optional(),
    // Accepts '' so an untouched field doesn't trip the 12-digit rule.
    aadhar_number: z.union([z.string().regex(/^\d{12}$/, 'Aadhar must be 12 digits'), z.literal('')])
        .nullable().optional(),
    bank_name: z.string().max(100).nullable().optional(),
    bank_account_number: z.string().max(30).nullable().optional(),
    ifsc_code: z.string().max(11).nullable().optional(),
    upi_id: z.string().max(100).nullable().optional(),
    name_on_account: z.string().max(150).nullable().optional(),
});
// Rate periods are month-granular — a rate applies to whole months and never
// prorates mid-month — so both bounds are YYYY-MM rather than dates.
const monthKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');
export const createRatePeriodSchema = z.object({
    rate: z.coerce.number().positive(),
    effective_from: monthKey,
    /** Omit or null for an open-ended period. */
    effective_to: monthKey.nullable().optional(),
}).refine((v) => !v.effective_to || v.effective_to >= v.effective_from, { message: 'End month cannot be before the start month', path: ['effective_to'] });
export const bulkUpdateMultipliersSchema = z.object({
    multipliers: z.array(z.object({
        day_of_week: z.number().int().min(0).max(6),
        rate: z.coerce.number().min(0).max(10),
        is_active: z.boolean(),
    })).length(7),
});
export const computeSalarySchema = z.object({
    user_id: z.string().uuid(),
    year: z.coerce.number().int().min(2020).max(2100),
    month: z.coerce.number().int().min(1).max(12),
});
export const createLoanSchema = z.object({
    user_id: z.string().uuid(),
    amount: z.coerce.number().positive(),
    num_emis: z.coerce.number().int().positive(),
    emi_amount: z.coerce.number().positive(),
    interest_rate: z.coerce.number().min(0).default(0),
    start_date: z.string().date(),
    reason: z.string().max(500).nullable().optional(),
});
export const updateLoanSchema = z.object({
    amount: z.coerce.number().positive().optional(),
    num_emis: z.coerce.number().int().positive().optional(),
    emi_amount: z.coerce.number().positive().optional(),
    interest_rate: z.coerce.number().min(0).optional(),
    start_date: z.string().date().optional(),
    reason: z.string().max(500).nullable().optional(),
});
export const loanStatusSchema = z.object({
    status: z.enum(['active', 'paused', 'closed']),
});
export const payEmiSchema = z.object({
    year: z.coerce.number().int().min(2020).max(2100),
    month: z.coerce.number().int().min(1).max(12),
    amount_paid: z.coerce.number().positive(),
    paid_at: z.string().optional(),
});
export const setLoanDeductionScheduleSchema = z.object({
    year: z.coerce.number().int().min(2020).max(2100),
    month: z.coerce.number().int().min(1).max(12),
    deduction_amount: z.coerce.number().positive(),
});
/** Replaces the schedule from `from_year`/`from_month` onward with equal monthly deductions. */
export const generateLoanScheduleSchema = z.object({
    from_year: z.coerce.number().int().min(2020).max(2100),
    from_month: z.coerce.number().int().min(1).max(12),
    monthly_amount: z.coerce.number().positive(),
});
export const createAdvanceSchema = z.object({
    user_id: z.string().uuid(),
    amount: z.coerce.number().positive(),
    advance_date: z.string().date(),
    deduction_year: z.coerce.number().int().min(2020).max(2100).nullable().optional(),
    deduction_month: z.coerce.number().int().min(1).max(12).nullable().optional(),
    reason: z.string().max(500).nullable().optional(),
});
export const updateAdvanceSchema = z.object({
    amount: z.coerce.number().positive().optional(),
    advance_date: z.string().date().optional(),
    deduction_year: z.coerce.number().int().min(2020).max(2100).nullable().optional(),
    deduction_month: z.coerce.number().int().min(1).max(12).nullable().optional(),
    reason: z.string().max(500).nullable().optional(),
});
export const listLoansQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['active', 'paused', 'closed']).optional(),
    user_id: z.string().uuid().optional(),
});
export const listAdvancesQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['pending', 'deducted', 'cancelled']).optional(),
    user_id: z.string().uuid().optional(),
});
export const listSalaryOverviewQuerySchema = paginationQuerySchema.extend({
    year: z.coerce.number().int().min(2020).max(2100),
    month: z.coerce.number().int().min(1).max(12),
});
export const listPenaltiesQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['pending', 'approved', 'rejected']).optional(),
    user_id: z.string().uuid().optional(),
    date_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    date_to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const rejectPenaltySchema = z.object({
    rejection_reason: z.string().min(1).max(500),
});
export const generatePenaltiesSchema = z.object({
    date: z.string().date().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
}).refine((v) => (v.date_from == null) === (v.date_to == null), { message: 'date_from and date_to must be provided together' });
export const createManualPenaltySchema = z.object({
    user_id: z.string().uuid(),
    attendance_date: z.string().date(),
    penalty_minutes: z.number().int().min(1).max(1440),
    reason: z.string().min(1).max(500),
});
// ── Paid Hours ─────────────────────────────────────────────────────────────────
export const createPaidHoursSchema = z.object({
    attendance_date: z.string().date(),
    hours: z.number().positive().max(24),
    reason: z.string().min(1).max(1000),
    user_ids: z.array(z.string().uuid()).min(1),
});
export const listPaidHoursQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['pending', 'approved', 'rejected']).optional(),
    user_id: z.string().uuid().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
});
export const rejectPaidHoursSchema = z.object({
    rejection_reason: z.string().min(1).max(500),
});
export const updatePenaltyConfigSchema = z.object({
    miss_punch_minutes: z.number().int().min(1).max(480),
    late_arrival_min_threshold: z.number().int().min(0).max(120),
    exclude_sundays_late_arrival: z.boolean(),
    exclude_sundays_miss_punch: z.boolean(),
    auto_generate_enabled: z.boolean(),
    penalty_gap_days: z.number().int().min(0).max(30),
    late_leave_correction_enabled: z.boolean(),
    penalty_rate_mode: z.enum(['per_hour', 'global_rate', 'multiplier', 'fixed_amount']),
    penalty_rate_per_hour: z.number().positive().nullable().optional(),
    penalty_rate_multiplier: z.number().positive().max(10).default(1.0),
    penalty_fixed_amount: z.number().positive().nullable().optional(),
});
export const tierRuleSchema = z.object({
    occurrence_number: z.number().int().min(0),
    penalty_minutes: z.number().int().min(0).max(1440),
    label: z.string().max(100).nullable().optional(),
});
export const upsertTierRulesSchema = z.object({
    rules: z
        .array(tierRuleSchema)
        .min(1, 'At least the default rule (occurrence_number=0) is required')
        .refine((rules) => rules.filter((r) => r.occurrence_number === 0).length === 1, 'Exactly one default rule (occurrence_number=0) is required'),
});
export const modifyPenaltySchema = z.object({
    penalty_minutes: z.number().int().min(0).max(1440),
    reason: z.string().min(1).max(500).optional(),
});
export const updatePayrollLAConfigSchema = z.object({
    max_loan_amount: z.number().positive().max(10_000_000),
    max_advance_amount: z.number().positive().max(1_000_000),
    default_interest_rate: z.number().min(0).max(100),
    max_loan_emis: z.number().int().min(1).max(120),
    max_active_loans: z.number().int().min(1),
    require_loan_reason: z.boolean(),
    require_advance_reason: z.boolean(),
});
// ── Organization Hierarchy ──────────────────────────────────────────────────────
export const createOrgNodeSchema = z.object({
    name: z.string().min(1).max(150),
    level_type_id: z.string().uuid(),
    parent_id: z.string().uuid().nullable().optional(),
    sort_order: z.coerce.number().int().min(0).default(0),
    description: z.string().max(1000).nullable().optional(),
});
export const updateOrgNodeSchema = createOrgNodeSchema.partial();
export const assignOrgDesignationSchema = z.object({
    designation_id: z.string().uuid(),
    user_id: z.string().uuid(),
});
export const createOrgLevelTypeSchema = z.object({
    name: z.string().min(1).max(100),
});
// ---- Recruitment ----
const candidateSourceSchema = z.enum([
    'referral', 'job_portal', 'walk_in', 'agency', 'social_media', 'campus', 'other',
]);
const candidateStageSchema = z.enum([
    'new', 'screening', 'interview', 'selected',
    'offer_released', 'offer_accepted', 'offer_rejected',
    'rejected', 'converted', 'on_hold',
]);
const processTypeSchema = z.enum(['joining', 'exit']);
// Free-form section label (custom sections allowed); slugified server-side is not
// required — stored as typed. Length-bounded to match the DB column.
const processSectionSchema = z.string().trim().min(1).max(60);
const stepStatusSchema = z.enum(['pending', 'completed', 'skipped']);
const candidateEducationItemSchema = z.object({
    degree: z.string().min(1).max(150),
    institute: z.string().max(200).nullable().optional(),
    year: z.string().max(10).nullable().optional(),
    percentage: z.coerce.number().min(0).max(100).nullable().optional(),
});
const candidateExperienceItemSchema = z.object({
    company_name: z.string().min(1).max(200),
    designation: z.string().max(150).nullable().optional(),
    start_date: z.string().date().nullable().optional(),
    end_date: z.string().date().nullable().optional(),
    is_current: z.boolean().optional(),
    ctc: z.coerce.number().min(0).nullable().optional(),
});
export const createCandidateSchema = z.object({
    first_name: z.string().min(1).max(100),
    last_name: z.string().max(100).nullable().optional(),
    email: z.string().email(),
    phone: z.string().min(1).max(20),
    alternate_phone: z.string().max(20).nullable().optional(),
    date_of_birth: z.string().date().nullable().optional(),
    gender: z.enum(['male', 'female', 'other']).nullable().optional(),
    marital_status: z.string().max(20).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    pincode: z.string().max(20).nullable().optional(),
    current_company: z.string().max(150).nullable().optional(),
    current_designation: z.string().max(150).nullable().optional(),
    total_experience_years: z.coerce.number().min(0).max(60).nullable().optional(),
    current_ctc: z.coerce.number().min(0).nullable().optional(),
    expected_ctc: z.coerce.number().min(0).nullable().optional(),
    notice_period_days: z.coerce.number().int().min(0).max(365).nullable().optional(),
    applied_department_id: z.string().uuid().nullable().optional(),
    applied_designation_id: z.string().uuid().nullable().optional(),
    source: candidateSourceSchema,
    source_details: z.string().max(255).nullable().optional(),
    referred_by: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    education: z.array(candidateEducationItemSchema).max(20).optional(),
    experience: z.array(candidateExperienceItemSchema).max(20).optional(),
});
export const updateCandidateSchema = createCandidateSchema.partial();
export const moveCandidateStageSchema = z.object({
    stage: candidateStageSchema,
    comment: z.string().max(1000).nullable().optional(),
    rejection_reason: z.string().max(500).nullable().optional(),
    on_hold_reason: z.string().max(500).nullable().optional(),
});
export const convertCandidateSchema = z.object({
    employee_code: z.string().min(1).max(50),
    password: z.string().min(8).max(72),
    role_codes: z.array(roleCodeSchema).min(1, 'At least one role required'),
    department_id: z.string().uuid().nullable().optional(),
    designation_id: z.string().uuid().nullable().optional(),
    joining_date: z.string().date(),
});
export const listCandidateQuerySchema = paginationQuerySchema.extend({
    stage: candidateStageSchema.optional(),
    source: candidateSourceSchema.optional(),
    department_id: z.string().uuid().optional(),
    designation_id: z.string().uuid().optional(),
});
export const updateProcessSettingsSchema = z.object({
    name: z.string().min(1).max(150).optional(),
    description: z.string().max(1000).nullable().optional(),
    is_active: z.boolean().optional(),
    auto_start_enabled: z.boolean().optional(),
    manual_trigger_allowed: z.boolean().optional(),
    allow_reinitiate: z.boolean().optional(),
    lock_employee_during_exit: z.boolean().optional(),
});
export const createStepTemplateSchema = z.object({
    section: processSectionSchema,
    step_name: z.string().min(1).max(150),
    instructions: z.string().max(1000).nullable().optional(),
    is_required: z.boolean().default(true),
    sla_hours: z.coerce.number().int().min(0).max(8760).nullable().optional(),
    step_order: z.coerce.number().int().min(0).default(0),
});
export const updateStepTemplateSchema = createStepTemplateSchema.partial().extend({
    is_active: z.boolean().optional(),
});
export const reorderStepsSchema = z.object({
    ordered_ids: z.array(z.string().uuid()).min(1),
});
export const createSectionSchema = z.object({
    name: z.string().trim().min(1).max(60),
});
export const updateSectionSchema = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    is_active: z.boolean().optional(),
});
export const reorderSectionsSchema = z.object({
    ordered_ids: z.array(z.string().uuid()).min(1),
});
export const startProcessSchema = z
    .object({
    user_id: z.string().uuid(),
    process_type: processTypeSchema,
    joining_date: z.string().date().nullable().optional(),
})
    .refine((v) => v.process_type !== 'joining' || !!v.joining_date, {
    message: 'Joining date is required for a joining process',
    path: ['joining_date'],
});
// ── Requirements ──────────────────────────────────────────────────────────────
export const createRequirementCategorySchema = z.object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(20),
    description: z.string().max(500).optional().nullable(),
});
export const updateRequirementCategorySchema = createRequirementCategorySchema.partial();
export const createRequirementStatusSchema = z.object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(50),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
    display_order: z.coerce.number().int().min(0).default(0),
    is_default: z.boolean().default(false),
});
export const updateRequirementStatusSchema = createRequirementStatusSchema.partial();
export const requirementPrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export const createRequirementSchema = z.object({
    title: z.string().min(1).max(255),
    description: z.string().max(5000).optional().nullable(),
    category_id: z.string().uuid(),
    status_id: z.string().uuid().optional().nullable(),
    priority: requirementPrioritySchema.default('medium'),
    quantity: z.coerce.number().positive().default(1),
    unit: z.string().min(1).max(50).default('pieces'),
    estimated_cost: z.coerce.number().min(0).optional().nullable(),
    approved_budget: z.coerce.number().min(0).optional().nullable(),
    due_date: z.string().date().optional().nullable(),
    assigned_to: z.string().uuid().optional().nullable(),
    notes: z.string().max(2000).optional().nullable(),
});
export const updateRequirementSchema = createRequirementSchema.partial().extend({
    rejection_reason: z.string().max(1000).optional().nullable(),
    fulfilled_date: z.string().date().optional().nullable(),
});
export const updateRequirementStatusInlineSchema = z.object({
    status_id: z.string().uuid(),
    rejection_reason: z.string().max(1000).optional().nullable(),
});
export const createRequirementCommentSchema = z.object({
    body: z.string().min(1).max(2000),
});
export const listRequirementsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    q: z.string().optional(),
    category_id: z.string().uuid().optional(),
    status_id: z.string().uuid().optional(),
    priority: requirementPrioritySchema.optional(),
    requested_by: z.string().uuid().optional(),
    assigned_to: z.string().uuid().optional(),
});
export const toggleStepSchema = z.object({
    status: stepStatusSchema,
    remarks: z.string().max(1000).nullable().optional(),
});
export const cancelProcessSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const listProcessInstanceQuerySchema = paginationQuerySchema.extend({
    process_type: processTypeSchema.optional(),
    status: z.enum(['in_progress', 'completed', 'cancelled']).optional(),
});
// ---- SOP (Standard Operating Procedures) ----
export const SOP_STATUSES = ['draft', 'active', 'archived'];
export const sopStatusSchema = z.enum(SOP_STATUSES);
export const createSopCategorySchema = z.object({
    name: z.string().min(1).max(150),
    description: z.string().max(500).nullable().optional(),
    color: z.string().max(20).nullable().optional(),
});
export const updateSopCategorySchema = createSopCategorySchema.partial();
// Content is HTML from a rich-text editor; capped to keep payloads sane (~2 MB).
const SOP_CONTENT_MAX = 2_000_000;
export const createSopSchema = z.object({
    title: z.string().min(1).max(200),
    category_id: z.string().uuid().nullable().optional(),
    summary: z.string().max(500).nullable().optional(),
    content: z.string().max(SOP_CONTENT_MAX).default(''),
    status: sopStatusSchema.default('draft'),
});
export const updateSopSchema = createSopSchema.partial();
export const updateSopStatusSchema = z.object({ status: sopStatusSchema });
export const listSopQuerySchema = paginationQuerySchema.extend({
    status: sopStatusSchema.optional(),
    category_id: z.string().uuid().optional(),
});
export const sopAssignmentTargetSchema = z.object({
    target_type: z.enum(['user', 'department']),
    target_id: z.string().uuid(),
});
export const createSopAssignmentsSchema = z.object({
    targets: z.array(sopAssignmentTargetSchema).min(1, 'Select at least one user or department'),
    is_mandatory: z.boolean().default(true),
    due_date: z.string().date().nullable().optional(),
});
export const acknowledgeSopSchema = z.object({ confirm: z.literal(true) });
export const listMySopQuerySchema = paginationQuerySchema.extend({
    filter: z.enum(['all', 'mandatory', 'pending', 'completed']).default('all'),
    department_id: z.string().uuid().optional(),
});
export const listNotificationsQuerySchema = paginationQuerySchema.extend({
    unread_only: z.coerce.boolean().optional(),
});
// ── ID Management ───────────────────────────────────────────────────────────
const idAccountIdentifierSchema = z.string().trim().min(1, 'Account ID is required').max(255);
const idAccountUsersSchema = z
    .array(z.string().uuid())
    .min(1, 'Select at least one user')
    .max(500)
    .transform((ids) => [...new Set(ids)]);
export const idCategorySchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(100),
});
export const createUserAccountIdSchema = z.object({
    category_id: z.string().uuid('Select a category'),
    account_identifier: idAccountIdentifierSchema,
    user_ids: idAccountUsersSchema,
});
export const updateUserAccountIdSchema = z.object({
    category_id: z.string().uuid('Select a category'),
    account_identifier: idAccountIdentifierSchema,
    user_ids: idAccountUsersSchema,
});
export const listUserAccountIdsQuerySchema = paginationQuerySchema.extend({
    user_id: z.string().uuid().optional(),
    category_id: z.string().uuid().optional(),
});
export const listMyAccountIdsQuerySchema = paginationQuerySchema.extend({
    category_id: z.string().uuid().optional(),
});
// ── Assets ──────────────────────────────────────────────────────────────────
export const createAssetCategorySchema = z.object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(20).toUpperCase(),
    description: z.string().max(500).optional(),
});
export const updateAssetCategorySchema = createAssetCategorySchema.partial();
export const createAssetSubcategorySchema = z.object({
    category_id: z.string().uuid(),
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(20).toUpperCase(),
    description: z.string().max(500).optional(),
});
export const updateAssetSubcategorySchema = createAssetSubcategorySchema.partial();
export const createAssetBrandSchema = z.object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(50).toUpperCase(),
    description: z.string().max(500).optional(),
});
export const updateAssetBrandSchema = createAssetBrandSchema.partial();
export const createAssetStatusSchema = z.object({
    name: z.string().min(1).max(100),
    code: z.string().min(1).max(50),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
    display_order: z.coerce.number().int().min(0).default(0),
    is_default: z.boolean().default(false),
});
export const updateAssetStatusSchema = createAssetStatusSchema.partial();
export const createAssetSchema = z.object({
    category_id: z.string().uuid(),
    subcategory_id: z.string().uuid().optional().nullable(),
    brand_id: z.string().uuid().optional().nullable(),
    model_name: z.string().max(255).optional().nullable(),
    purchase_date: z.string().date().optional().nullable(),
    warranty_available: z.boolean().default(false),
    warranty_end_date: z.string().date().optional().nullable(),
    status_id: z.string().uuid(),
    assigned_to_user_id: z.string().uuid().optional().nullable(),
    assigned_department_id: z.string().uuid().optional().nullable(),
    notes: z.string().max(2000).optional().nullable(),
});
// One form, N identical assets — each copy gets its own asset code.
export const bulkCreateAssetSchema = createAssetSchema.extend({
    quantity: z.coerce
        .number({ invalid_type_error: 'Quantity is required' })
        .int('Quantity must be a whole number')
        .min(1, 'Quantity must be at least 1'),
});
export const updateAssetSchema = createAssetSchema.partial();
export const updateAssetStatusInlineSchema = z.object({
    status_id: z.string().uuid(),
});
export const listAssetsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    q: z.string().trim().optional(),
    category_id: z.string().uuid().optional(),
    subcategory_id: z.string().uuid().optional(),
    status_id: z.string().uuid().optional(),
    brand_id: z.string().uuid().optional(),
    department_id: z.string().uuid().optional(),
    assigned_to_user_id: z.string().uuid().optional(),
});
export const updateAssetConfigSchema = z.object({
    value: z.string().min(1).max(20),
});
// ── Task Management ──────────────────────────────────────────────────────────
export const TASK_TYPES = ['daily', 'weekly', 'monthly', 'yearly', 'periodic'];
export const taskTypeSchema = z.enum(TASK_TYPES);
/**
 * Types whose Complete goes through a reviewer instead of finishing the occurrence.
 * `daily` is deliberately absent — a daily task would need a review action every day,
 * so it still completes in one step.
 *
 * `periodic` is what the UI labels "One Time": migration 208 folded the old `one_time`
 * type into a single-day periodic task, so this list is the requested
 * week / month / year / one-time set.
 */
export const REVIEWABLE_TASK_TYPES = ['weekly', 'monthly', 'yearly', 'periodic'];
export const TASK_STATUSES = [
    'pending_accept', 'in_progress', 'for_review',
    'needs_improvement', 'completed', 'discarded',
];
export const taskStatusSchema = z.enum(TASK_STATUSES);
// Range-checked, not just shaped: `\d{2}:\d{2}` also accepts '99:99', and a config
// consumer that turns that into minutes-past-midnight gets 6039 — past any real time of
// day, so a scheduled job gated on it would silently never run.
const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (00:00–23:59)');
// Recurrence config is type-discriminated; we validate cross-field in a refine
// so a `daily` task can't carry a `monthly_day`, etc. Multi-value selections
// (weekly weekdays, periodic dates) come as arrays and are persisted to the
// normalized `task_recurrence_days` child table server-side.
const taskBaseShape = {
    title: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    task_type: taskTypeSchema,
    is_priority: z.boolean().default(false),
    reminder_enabled: z.boolean().default(false),
    // Important task: assignee_ids is an ordered fallback chain, not a parallel set.
    is_important: z.boolean().default(false),
    start_time: timeOfDaySchema.nullable().optional(),
    end_time: timeOfDaySchema.nullable().optional(),
    // type-specific (all optional at the shape level; refined below)
    monthly_day: z.coerce.number().int().min(1).max(28).nullable().optional(),
    yearly_month: z.coerce.number().int().min(1).max(12).nullable().optional(),
    yearly_day: z.coerce.number().int().min(1).max(31).nullable().optional(),
    periodic_mode: z.enum(['dates', 'weekdays']).nullable().optional(),
    periodic_days: z.array(z.coerce.number().int().min(1).max(31)).max(31).optional(),
    weekly_weekdays: z.array(z.coerce.number().int().min(0).max(6)).max(7).optional(),
    periodic_start_date: z.string().date().nullable().optional(),
    periodic_end_date: z.string().date().nullable().optional(),
};
function refineTaskRecurrence(v, ctx) {
    switch (v.task_type) {
        case 'monthly':
            if (v.monthly_day == null)
                ctx.addIssue({ code: 'custom', path: ['monthly_day'], message: 'Select a date (1–28)' });
            break;
        case 'yearly':
            if (v.yearly_month == null)
                ctx.addIssue({ code: 'custom', path: ['yearly_month'], message: 'Select a month' });
            if (v.yearly_day == null)
                ctx.addIssue({ code: 'custom', path: ['yearly_day'], message: 'Select a date' });
            break;
        case 'weekly':
            if (!v.weekly_weekdays || v.weekly_weekdays.length === 0)
                ctx.addIssue({ code: 'custom', path: ['weekly_weekdays'], message: 'Select at least one weekday' });
            break;
        case 'periodic':
            if (!v.periodic_start_date)
                ctx.addIssue({ code: 'custom', path: ['periodic_start_date'], message: 'Start date is required for one-time tasks' });
            if (!v.periodic_end_date)
                ctx.addIssue({ code: 'custom', path: ['periodic_end_date'], message: 'End date is required for one-time tasks' });
            if (v.periodic_start_date && v.periodic_end_date && v.periodic_end_date < v.periodic_start_date)
                ctx.addIssue({ code: 'custom', path: ['periodic_end_date'], message: 'End date must be after start date' });
            if (!v.periodic_mode) {
                ctx.addIssue({ code: 'custom', path: ['periodic_mode'], message: 'Choose a selection mode' });
                break;
            }
            if (v.periodic_mode === 'dates' && (!v.periodic_days || v.periodic_days.length === 0))
                ctx.addIssue({ code: 'custom', path: ['periodic_days'], message: 'Select at least one date' });
            if (v.periodic_mode === 'weekdays' && (!v.weekly_weekdays || v.weekly_weekdays.length === 0))
                ctx.addIssue({ code: 'custom', path: ['weekly_weekdays'], message: 'Select at least one weekday' });
            break;
        // daily: no extra config
    }
}
/**
 * An important task is a fallback chain — it goes to one person at a time and falls
 * through to the next when the first is not punched in. One person leaves nothing to fall
 * through to, and it is worse than a plain task: when that person is not punched in the
 * holder resolves to nobody and the task hides itself from everyone for the day.
 */
export const MIN_IMPORTANT_ASSIGNEES = 2;
export const IMPORTANT_CHAIN_TOO_SHORT = `An important task needs at least ${MIN_IMPORTANT_ASSIGNEES} people in its sequence. `
    + 'Add someone else first, or turn off Important.';
export const createTaskSchema = z
    .object({
    ...taskBaseShape,
    assignee_ids: z.array(z.string().uuid()).min(1, 'Assign at least one user'),
})
    .superRefine(refineTaskRecurrence)
    // Chained rather than folded into refineTaskRecurrence: that function's parameter type is
    // purely recurrence fields, and this has nothing to do with recurrence.
    //
    // NOT sufficient on its own — this array is not de-duplicated, so [u1, u1] passes and
    // then collapses to a single roster row. createTask re-checks the deduped set.
    .superRefine((v, ctx) => {
    if (v.is_important && v.assignee_ids.length < MIN_IMPORTANT_ASSIGNEES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['assignee_ids'],
            message: IMPORTANT_CHAIN_TOO_SHORT,
        });
    }
});
export const updateTaskSchema = z
    .object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    is_priority: z.boolean().optional(),
    reminder_enabled: z.boolean().optional(),
    is_important: z.boolean().optional(),
    start_time: timeOfDaySchema.nullable().optional(),
    end_time: timeOfDaySchema.nullable().optional(),
});
export const listTasksQuerySchema = paginationQuerySchema.extend({
    task_type: taskTypeSchema.optional(),
    status: taskStatusSchema.optional(),
    lifecycle_state: z.enum(['active', 'stopped', 'archived']).optional(),
    department_id: z.string().uuid().optional(),
    assignee_id: z.string().uuid().optional(),
    created_by: z.string().uuid().optional(),
    is_priority: z.coerce.boolean().optional(),
});
/**
 * The reviewer's queue. Scope is implicit — the server returns only submissions on
 * tasks the caller created — so there is no `created_by` filter to spoof here.
 */
export const listTaskReviewQuerySchema = paginationQuerySchema.extend({
    task_type: taskTypeSchema.optional(),
    department_id: z.string().uuid().optional(),
    assignee_id: z.string().uuid().optional(),
});
export const addTaskAssigneesSchema = z.object({
    assignee_ids: z.array(z.string().uuid()).min(1),
});
// Full re-ordering of an important task's fallback chain — array position = sequence.
export const reorderTaskAssigneesSchema = z.object({
    assignee_ids: z.array(z.string().uuid()).min(1),
});
/**
 * Statuses that cannot be set without a written reason, and the wording each one uses.
 *
 * Both close a door on someone else's work — one ends it, the other sends it back — so
 * the person on the receiving end is owed an explanation. Single source of truth: the API
 * rejects these, and the forms resolve against this same schema rather than restating it.
 */
export const REMARK_REQUIRED_STATUSES = {
    discarded: 'A remark is required when discarding a task',
    needs_improvement: 'Say what needs improving before sending this back',
};
// The creator may move an assignee's occurrence to any status (a deliberate override).
// The per-role narrowing — an assignee may not discard, nor approve their own work — is
// enforced in the service, which knows who is calling.
export const setOccurrenceStatusSchema = z
    .object({
    status: taskStatusSchema,
    // .trim() matters: without it a whitespace-only remark passes the refine below.
    note: z.string().trim().max(1000).nullable().optional(),
})
    .superRefine((val, ctx) => {
    const message = REMARK_REQUIRED_STATUSES[val.status];
    if (message && !val.note) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['note'] });
    }
});
export const taskReportQuerySchema = z.object({
    date: z.string().date(),
    department_id: z.string().uuid().optional(),
    q: z.string().optional(),
});
// ── Additional Tasks (self-logged work) ──
export const createAdditionalTaskSchema = z
    .object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    user_id: z.string().uuid().optional(), // defaults to caller server-side
    work_date: z.string().date(),
    start_time: timeOfDaySchema,
    end_time: timeOfDaySchema,
    assigned_by: z.string().uuid().nullable().optional(),
})
    .refine((v) => v.end_time > v.start_time, {
    message: 'End time must be after start time',
    path: ['end_time'],
});
export const updateAdditionalTaskSchema = z
    .object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    work_date: z.string().date().optional(),
    start_time: timeOfDaySchema.optional(),
    end_time: timeOfDaySchema.optional(),
    assigned_by: z.string().uuid().nullable().optional(),
});
export const listAdditionalTasksQuerySchema = paginationQuerySchema.extend({
    user_id: z.string().uuid().optional(),
    assigned_by: z.string().uuid().optional(),
    created_by: z.string().uuid().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
});
// ── Task Config ──
export const updateTaskTypeSettingSchema = z.object({
    is_enabled: z.boolean(),
});
// Module-level task settings (consumed by the Phase 2/3 engine). All optional —
// only provided keys are upserted.
export const updateTaskConfigSchema = z.object({
    reset_time: timeOfDaySchema.optional(),
    reset_timezone: z.string().min(1).max(64).optional(),
    reminder_global_enabled: z.boolean().optional(),
    reminder_lead_minutes: z.coerce.number().int().min(0).max(1440).optional(),
    report_snapshot_time: timeOfDaySchema.optional(),
    report_retention_days: z.coerce.number().int().min(1).max(3650).optional(),
});
// Admin "run now" trigger for the scheduler. `date` overrides the IST today the
// roll operates on — used to verify a specific day without waiting for the cron.
export const runTaskSchedulerSchema = z.object({
    date: z.string().date().optional(),
});
// ── Finance · Vendors ──────────────────────────────────────────────────────────
const optStr = (max) => z.string().trim().max(max).optional().nullable();
export const createVendorSchema = z.object({
    name: z.string().trim().min(1).max(150),
    type: z.string().trim().max(50).default('Other'),
    contact_person: optStr(150),
    mobile: optStr(20),
    email: optStr(150),
    gst_number: optStr(20),
    pan: optStr(10),
    bank_account_name: optStr(150),
    bank_account_number: optStr(30),
    ifsc: optStr(11),
    upi_id: optStr(100),
    credit_limit: z.coerce.number().min(0).default(0),
    opening_balance: z.coerce.number().min(0).default(0),
    is_active: z.boolean().default(true),
});
// Opening balance is immutable after creation — it seeds the ledger, so it is
// omitted from the update schema to keep the ledger from drifting.
export const updateVendorSchema = createVendorSchema.omit({ opening_balance: true }).partial();
export const listVendorsQuerySchema = paginationQuerySchema.extend({
    type: z.string().optional(),
    is_active: z.enum(['true', 'false']).optional(),
});
// ── Finance · Purchases & Payments ───────────────────────────────────────────────
// Fields arrive as multipart/form-data (alongside the optional invoice file), so
// numbers are coerced from strings. `invoice_url` is set server-side from the upload.
export const createPurchaseSchema = z.object({
    vendor_id: z.string().uuid(),
    category_id: z.string().uuid().optional().nullable(),
    invoice_number: z.string().trim().min(1).max(100),
    invoice_date: z.string().date().optional().nullable(),
    purchase_date: z.string().date().optional().nullable(),
    base_amount: z.coerce.number().positive(),
    gst_amount: z.coerce.number().min(0).default(0),
    description: optStr(2000),
});
export const PURCHASE_STATUSES = ['Pending', 'Partially Paid', 'Paid'];
export const recordPaymentSchema = z.object({
    payment_amount: z.coerce.number().positive(),
    payment_mode: z.string().trim().min(1).max(50),
    transaction_id: z.string().trim().min(1).max(100),
    payment_date: z.string().date().optional().nullable(),
    remark: optStr(500),
});
export const listPurchasesQuerySchema = paginationQuerySchema.extend({
    vendor_id: z.string().uuid().optional(),
    category_id: z.string().uuid().optional(),
    status: z.enum(PURCHASE_STATUSES).optional(),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
});
// ── Finance · Ledger ─────────────────────────────────────────────────────────────
export const listLedgerQuerySchema = paginationQuerySchema.extend({
    vendor_id: z.string().uuid().optional(),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
});
// ── Finance · Master (categories, payment modes, system rules, audit) ─────────────
export const createPurchaseCategorySchema = z.object({
    name: z.string().trim().min(1).max(100),
    monthly_budget: z.coerce.number().min(0).default(0),
    max_purchase_limit: z.coerce.number().min(0).default(0),
    gst_applicable: z.boolean().default(false),
});
export const updatePurchaseCategorySchema = createPurchaseCategorySchema.partial();
export const createPaymentModeSchema = z.object({
    mode_name: z.string().trim().min(1).max(50),
});
export const updatePaymentModeSchema = z.object({
    mode_name: z.string().trim().min(1).max(50).optional(),
    is_active: z.boolean().optional(),
});
export const FINANCE_RULE_KEYS = [
    'enforce_invoice_upload', 'allow_partial_payment', 'enforce_credit_limit', 'enable_gst_auto_calc',
];
// Boolean rules carry 'true'/'false'; enforce_credit_limit carries 'strict'/'warning'.
export const updateFinanceRuleSchema = z.object({
    value: z.enum(['true', 'false', 'strict', 'warning']),
});
export const listFinanceAuditQuerySchema = paginationQuerySchema.extend({
    module: z.string().optional(),
});
// ── Finance · Reimbursements ──────────────────────────────────────────────────────
export const createReimbursementAccountSchema = z.object({
    user_id: z.string().uuid(),
});
export const creditReimbursementSchema = z.object({
    amount: z.coerce.number().positive(),
    remark: optStr(500),
});
export const createReimbursementCategorySchema = z.object({
    name: z.string().trim().min(1).max(100),
    monthly_limit: z.coerce.number().min(0).default(0),
    per_claim_limit: z.coerce.number().min(0).default(0),
    bill_required_above: z.coerce.number().min(0).default(0),
    gst_applicable: z.boolean().default(false),
});
export const updateReimbursementCategorySchema = createReimbursementCategorySchema.partial();
// Submitted by employees via multipart (with optional bill files); numbers coerced.
export const addReimbursementExpenseSchema = z.object({
    category_id: z.string().uuid().optional().nullable(),
    amount: z.coerce.number().positive(),
    expense_date: z.string().date().optional().nullable(),
    description: optStr(500),
    project_tag: optStr(100),
});
export const listReimbursementLedgerQuerySchema = paginationQuerySchema.extend({
    user_id: z.string().uuid().optional(),
});
// ── Notice ────────────────────────────────────────────────────────────────────
export const createNoticeSchema = z.object({
    title: z.string().trim().min(1).max(255),
    content: z.string().trim().min(1),
    notice_type: z.enum(['general', 'urgent', 'policy', 'event', 'circular']).default('general'),
    priority: z.enum(['low', 'normal', 'high', 'critical']).default('normal'),
    target_type: z.enum(['all', 'specific']).default('all'),
    requires_acknowledgment: z.boolean().default(false),
    expires_at: z.string().datetime({ offset: true }).nullable().optional(),
    scheduled_for: z.string().datetime({ offset: true }).nullable().optional(),
    recipient_ids: z.array(z.object({
        recipient_type: z.enum(['user', 'department']),
        recipient_id: z.string().uuid(),
    })).optional(),
});
export const updateNoticeSchema = createNoticeSchema.partial();
export const listNoticesQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['draft', 'published', 'archived']).optional(),
    notice_type: z.enum(['general', 'urgent', 'policy', 'event', 'circular']).optional(),
    priority: z.enum(['low', 'normal', 'high', 'critical']).optional(),
});
export const listNoticeReadsQuerySchema = paginationQuerySchema;
// ── Subscriptions ─────────────────────────────────────────────────────────────
export const createSubscriptionCategorySchema = z.object({
    name: z.string().trim().min(1).max(100),
    description: optStr(500),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color').default('#6B7280'),
});
export const updateSubscriptionCategorySchema = createSubscriptionCategorySchema.partial();
export const createSubscriptionCurrencySchema = z.object({
    code: z.string().trim().min(1).max(10).toUpperCase(),
    symbol: z.string().trim().min(1).max(10),
    name: z.string().trim().min(1).max(100),
    is_default: z.boolean().default(false),
});
export const updateSubscriptionCurrencySchema = createSubscriptionCurrencySchema.partial();
export const createSubscriptionStatusSchema = z.object({
    name: z.string().trim().min(1).max(100),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
    is_default: z.boolean().default(false),
    display_order: z.coerce.number().int().min(0).default(0),
});
export const updateSubscriptionStatusSchema = createSubscriptionStatusSchema.partial();
export const createSubscriptionBillingCycleSchema = z.object({
    name: z.string().trim().min(1).max(100),
    code: z.string().trim().min(1).max(50),
    interval_months: z.coerce.number().int().min(1).optional().nullable(),
    interval_days: z.coerce.number().int().min(1).optional().nullable(),
    is_recurring: z.boolean().default(true),
});
export const updateSubscriptionBillingCycleSchema = createSubscriptionBillingCycleSchema.partial();
export const createSubscriptionSchema = z.object({
    name: z.string().trim().min(1).max(200),
    vendor_name: optStr(200),
    category_id: z.string().uuid().optional().nullable(),
    department_id: z.string().uuid().optional().nullable(),
    assigned_to: z.string().uuid().optional().nullable(),
    billing_cycle_id: z.string().uuid(),
    custom_interval_days: z.coerce.number().int().min(1).optional().nullable(),
    amount: z.coerce.number().positive('Amount must be greater than 0'),
    currency_id: z.string().uuid(),
    start_date: z.string().date(),
    end_date: z.string().date().optional().nullable(),
    status_id: z.string().uuid(),
    reminder_days: z.coerce.number().int().min(0).max(365).default(7),
    auto_renewal: z.boolean().default(false),
    notes: optStr(2000),
});
export const updateSubscriptionSchema = createSubscriptionSchema.omit({ start_date: true }).partial();
export const renewSubscriptionSchema = z.object({
    amount: z.coerce.number().positive().optional(),
    notes: optStr(1000),
});
export const cancelSubscriptionSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
export const listSubscriptionsQuerySchema = paginationQuerySchema.extend({
    q: z.string().optional(),
    status_id: z.string().uuid().optional(),
    category_id: z.string().uuid().optional(),
    department_id: z.string().uuid().optional(),
    billing_cycle_id: z.string().uuid().optional(),
    due_from: z.string().date().optional(),
    due_to: z.string().date().optional(),
    sort: z.string().optional(),
});
export const listSubscriptionAuditQuerySchema = paginationQuerySchema.extend({
    subscription_id: z.string().uuid().optional(),
    action: z.string().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
});
// ── Tech Support ───────────────────────────────────────────────────────────────
export const createTicketSchema = z.object({
    title: z.string().trim().min(1, 'Title is required').max(255),
    description: z.string().trim().max(5000).nullable().optional(),
    category_id: z.string().uuid().nullable().optional(),
    priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
});
export const updateTicketSchema = z.object({
    title: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(5000).nullable().optional(),
    category_id: z.string().uuid().nullable().optional(),
    priority: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
});
export const changeTicketStatusSchema = z.object({
    status: z.enum(['open', 'in_progress', 'waiting', 'resolved', 'closed']),
    note: z.string().trim().max(500).nullable().optional(),
});
export const assignTicketSchema = z.object({
    assigned_to: z.string().uuid().nullable(),
    note: z.string().trim().max(500).nullable().optional(),
});
export const addTicketCommentSchema = z.object({
    comment_text: z.string().trim().min(1, 'Comment is required').max(5000),
    is_internal: z.boolean().default(false),
});
export const escalateTicketSchema = z.object({
    escalated_to: z.string().uuid(),
    reason: z.string().trim().min(1, 'Reason is required').max(1000),
});
export const listTicketsQuerySchema = paginationQuerySchema.extend({
    q: z.string().optional(),
    status: z.enum(['open', 'in_progress', 'waiting', 'resolved', 'closed']).optional(),
    priority: z.enum(['low', 'medium', 'high', 'urgent']).optional(),
    category_id: z.string().uuid().optional(),
    assigned_to: z.string().uuid().optional(),
    created_by: z.string().uuid().optional(),
    overdue: z.coerce.boolean().optional(),
    sort: z.string().optional(),
});
export const createSupportCategorySchema = z.object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500).nullable().optional(),
});
export const updateSupportCategorySchema = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    is_active: z.boolean().optional(),
});
// ── Goals ─────────────────────────────────────────────────────────────────────
const goalTargetTypeEnum = z.enum(['numeric', 'percentage', 'currency', 'milestone']);
const goalDirectionEnum = z.enum(['increase', 'decrease']);
const goalPriorityEnum = z.enum(['low', 'medium', 'high']);
export const goalMilestoneInputSchema = z.object({
    title: z.string().trim().min(1, 'Milestone title is required').max(200),
});
/** Appending a milestone to an existing goal takes the same shape as creating one. */
export const addGoalMilestoneSchema = goalMilestoneInputSchema;
/** Max checklist entries on a milestone goal — shared by the create payload, the
 *  add-milestone endpoint and the create form. */
export const GOAL_MILESTONE_MAX = 50;
/** Max employees one create-goal call may fan out to (one goal is created per owner). */
export const GOAL_ASSIGNEES_MAX = 50;
/**
 * Cross-field invariants for a goal's target definition. Shared so the create form,
 * the create endpoint, and the update endpoint (which merges a partial body onto the
 * stored row before checking) all enforce exactly the same rules.
 * Returns the first violation as a message, or null when the shape is valid.
 */
export function validateGoalTargets(goal) {
    if (goal.due_date < goal.start_date) {
        return 'Due date cannot be before the start date';
    }
    if (goal.target_type === 'milestone') {
        if (goal.target_value !== null) {
            return 'Milestone goals track a checklist and cannot have a numeric target';
        }
        if (goal.milestone_count < 1) {
            return 'A milestone goal needs at least one checklist item';
        }
        return null;
    }
    if (goal.target_value === null) {
        return 'Target value is required for this target type';
    }
    if (goal.target_type === 'percentage') {
        if (goal.target_value < 0 || goal.target_value > 100) {
            return 'Percentage target must be between 0 and 100';
        }
        if (goal.start_value < 0 || goal.start_value > 100) {
            return 'Percentage starting value must be between 0 and 100';
        }
    }
    if (goal.direction === 'increase' && goal.target_value <= goal.start_value) {
        return 'For an increase goal the target must be greater than the starting value';
    }
    if (goal.direction === 'decrease' && goal.target_value >= goal.start_value) {
        return 'For a decrease goal the target must be less than the starting value';
    }
    return null;
}
export const createGoalSchema = z
    .object({
    /**
     * Owners of the goal. Empty means a self-set goal; naming anyone else requires
     * goals.assign. More than one owner fans out into one independent goal per person,
     * so each of them tracks their own progress.
     */
    user_ids: z.array(z.string().uuid()).max(GOAL_ASSIGNEES_MAX).default([]),
    category_id: z.string().uuid().nullable().optional(),
    title: z.string().trim().min(1, 'Title is required').max(200),
    description: z.string().trim().max(5000).nullable().optional(),
    target_type: goalTargetTypeEnum,
    start_value: z.coerce.number().finite().default(0),
    target_value: z.coerce.number().finite().nullable().optional(),
    unit: z.string().trim().max(50).nullable().optional(),
    direction: goalDirectionEnum.default('increase'),
    weight: z.coerce.number().int().min(1).max(100).default(100),
    priority: goalPriorityEnum.default('medium'),
    start_date: z.string().date(),
    due_date: z.string().date(),
    status: z.enum(['draft', 'active']).default('active'),
    milestones: z.array(goalMilestoneInputSchema).max(GOAL_MILESTONE_MAX).default([]),
})
    .superRefine((val, ctx) => {
    const message = validateGoalTargets({
        target_type: val.target_type,
        start_value: val.start_value,
        target_value: val.target_value ?? null,
        direction: val.direction,
        start_date: val.start_date,
        due_date: val.due_date,
        milestone_count: val.milestones.length,
    });
    if (message) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: val.target_type === 'milestone' ? ['milestones'] : ['target_value'],
        });
    }
});
/**
 * Partial update. Target invariants are re-checked server-side against the merged
 * row (the client may send `target_value` without `target_type`), so only the
 * self-contained field rules live here.
 */
export const updateGoalSchema = z.object({
    category_id: z.string().uuid().nullable().optional(),
    title: z.string().trim().min(1, 'Title is required').max(200).optional(),
    description: z.string().trim().max(5000).nullable().optional(),
    target_type: goalTargetTypeEnum.optional(),
    start_value: z.coerce.number().finite().optional(),
    target_value: z.coerce.number().finite().nullable().optional(),
    unit: z.string().trim().max(50).nullable().optional(),
    direction: goalDirectionEnum.optional(),
    weight: z.coerce.number().int().min(1).max(100).optional(),
    priority: goalPriorityEnum.optional(),
    start_date: z.string().date().optional(),
    due_date: z.string().date().optional(),
});
// ── Goal automation config ─────────────────────────────────────────────────────
/**
 * Operator-editable half of `goal_config`. `last_run_at` / `last_run_summary` are
 * deliberately absent: the sweep writes those, so leaving them out of the schema is
 * what stops a client from forging a run record.
 */
export const updateGoalConfigSchema = z.object({
    sweep_enabled: z.boolean().optional(),
    sweep_run_time: timeOfDaySchema.optional(),
    auto_miss_enabled: z.boolean().optional(),
    reminder_enabled: z.boolean().optional(),
    // Sorted descending so reminders read "7 days, then 1 day" wherever they're shown,
    // and de-duplicated because two equal offsets would fire twice for one goal.
    reminder_offset_days: z.array(z.coerce.number().int().min(0).max(365))
        .min(1, 'Add at least one reminder offset')
        .max(6, 'At most 6 reminder offsets')
        .refine((a) => new Set(a).size === a.length, 'Offsets must be unique')
        .transform((a) => [...a].sort((x, y) => y - x))
        .optional(),
});
/** Admin "run now". `date` overrides the IST today and bypasses the run-time gate. */
export const runGoalSweepSchema = z.object({
    date: z.string().date().optional(),
});
/**
 * `achieved` and `missed` are not settable here — the first comes from the review
 * endpoint, the second only from the nightly overdue sweep.
 */
export const changeGoalStatusSchema = z.object({
    status: z.enum(['active', 'submitted', 'cancelled']),
    note: z.string().trim().max(500).nullable().optional(),
});
/**
 * One additive progress entry. `amount` is the movement toward the target, not the new
 * total: on a decrease goal the user types a positive magnitude and the server stores it
 * negative. Negative amounts are accepted on purpose — a logged entry can never be edited
 * or deleted, so offsetting one is the only way to correct an over-log. The amount itself
 * is deliberately uncapped.
 */
export const logGoalProgressSchema = z.object({
    amount: z.coerce
        .number()
        .finite()
        .refine((v) => v !== 0, 'Enter an amount other than zero'),
    note: z.string().trim().max(1000).nullable().optional(),
    logged_on: z.string().date().optional(),
});
export const toggleGoalMilestoneSchema = z.object({
    is_done: z.boolean(),
    note: z.string().trim().max(1000).nullable().optional(),
});
export const reviewGoalSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    rating: z.coerce.number().int().min(1).max(5).nullable().optional(),
    comment: z.string().trim().max(2000).nullable().optional(),
})
    .superRefine((val, ctx) => {
    if (val.decision === 'approve' && (val.rating === null || val.rating === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A rating from 1 to 5 is required when approving a goal',
            path: ['rating'],
        });
    }
    if (val.decision === 'reject' && !val.comment) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Explain what still needs work when sending a goal back',
            path: ['comment'],
        });
    }
});
export const listGoalsQuerySchema = paginationQuerySchema.extend({
    q: z.string().optional(),
    status: z.enum(['draft', 'active', 'submitted', 'achieved', 'missed', 'cancelled']).optional(),
    target_type: goalTargetTypeEnum.optional(),
    priority: goalPriorityEnum.optional(),
    category_id: z.string().uuid().optional(),
    user_id: z.string().uuid().optional(),
    department_id: z.string().uuid().optional(),
    /** `true` narrows to goals past their due date that are not yet achieved/cancelled. */
    overdue: z.coerce.boolean().optional(),
    /** Restrict to goals the caller owns, regardless of their view scope. */
    mine: z.coerce.boolean().optional(),
    due_after: z.string().date().optional(),
    due_before: z.string().date().optional(),
    sort: z.string().optional(),
});
export const createGoalCategorySchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(100),
    description: z.string().trim().max(500).nullable().optional(),
});
export const updateGoalCategorySchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(100).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    is_active: z.boolean().optional(),
});
// ── DM Team ───────────────────────────────────────────────────────────────────
export const createDmPageSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).nullable().optional(),
});
export const updateDmPageSchema = createDmPageSchema.partial().extend({
    is_active: z.boolean().optional(),
});
export const createDmShootingCategorySchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).nullable().optional(),
    requires_location: z.boolean().default(true),
});
export const updateDmShootingCategorySchema = createDmShootingCategorySchema.partial().extend({
    is_active: z.boolean().optional(),
});
export const dmDesignCategorySizeSchema = z.object({
    name: z.string().trim().min(1).max(500),
    sort_order: z.coerce.number().int().min(0).default(0),
});
export const createDmDesignCategorySchema = z.object({
    name: z.string().trim().min(1).max(200),
    sizes: z.array(dmDesignCategorySizeSchema).optional(),
});
export const updateDmDesignCategorySchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    is_active: z.boolean().optional(),
});
export const addDmDesignCategorySizeSchema = z.object({
    name: z.string().trim().min(1).max(500),
    sort_order: z.coerce.number().int().min(0).default(0),
});
export const updateDmDesignCategorySizeSchema = z.object({
    name: z.string().trim().min(1).max(500).optional(),
    sort_order: z.coerce.number().int().min(0).optional(),
});
const dmDesignProjectStatusSchema = z.enum(['pending', 'in_progress', 'in_hold', 'completed', 'cancelled']);
export const createDmDesignProjectSchema = z.object({
    title: z.string().trim().min(1).max(500),
    description: z.string().trim().max(5000).nullable().optional(),
    publish_date: z.string().date().nullable().optional(),
    start_date: z.string().date().nullable().optional(),
    end_date: z.string().date().nullable().optional(),
    designer_id: z.string().uuid().nullable().optional(),
    assigned_by_id: z.string().uuid().nullable().optional(),
    categories: z.array(z.object({
        category_id: z.string().uuid().nullable().optional(),
        size_id: z.string().uuid().nullable().optional(),
        custom_category_text: z.string().trim().min(1).max(500).optional(),
        custom_size_text: z.string().trim().max(500).nullable().optional(),
    })).min(1, 'At least one category required'),
});
export const updateDmDesignProjectSchema = z.object({
    title: z.string().trim().min(1).max(500).optional(),
    description: z.string().trim().max(5000).nullable().optional(),
    publish_date: z.string().date().nullable().optional(),
    start_date: z.string().date().nullable().optional(),
    end_date: z.string().date().nullable().optional(),
    designer_id: z.string().uuid().nullable().optional(),
    assigned_by_id: z.string().uuid().nullable().optional(),
    categories: z.array(z.object({
        category_id: z.string().uuid().nullable().optional(),
        size_id: z.string().uuid().nullable().optional(),
        custom_category_text: z.string().trim().min(1).max(500).optional(),
        custom_size_text: z.string().trim().max(500).nullable().optional(),
    })).optional(),
});
export const updateDmDesignProjectStatusSchema = z.object({
    status: dmDesignProjectStatusSchema,
    hold_reason: z.string().trim().max(2000).nullable().optional(),
    location: z.string().trim().max(500).optional(),
});
export const listDmDesignProjectsQuerySchema = paginationQuerySchema.extend({
    tab: z.enum(['active', 'completed', 'cancelled']).optional(),
    status: dmDesignProjectStatusSchema.optional(),
    designer_id: z.string().uuid().optional(),
    assigned_by_id: z.string().uuid().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
});
export const reorderDmDesignProjectsSchema = z.object({
    items: z.array(z.object({
        id: z.string().uuid(),
        order_index: z.number().int().min(0),
    })).min(1),
});
const dmActivityStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'cancelled']);
export const createDmActivitySchema = z.object({
    title: z.string().trim().min(1).max(500),
    shooting_category_id: z.string().uuid().nullable().optional(),
    location: z.string().trim().max(1000).nullable().optional(),
    date_time: z.string().datetime({ offset: true }),
    page_id: z.string().uuid().nullable().optional(),
    assigned_user_ids: z.array(z.string().uuid()).optional(),
});
export const updateDmActivitySchema = z.object({
    title: z.string().trim().min(1).max(500).optional(),
    shooting_category_id: z.string().uuid().nullable().optional(),
    location: z.string().trim().max(1000).nullable().optional(),
    date_time: z.string().datetime({ offset: true }).optional(),
    updated_page_id: z.string().uuid().nullable().optional(),
});
export const updateDmActivityStatusSchema = z.object({
    status: dmActivityStatusSchema,
});
export const assignDmActivitySchema = z.object({
    user_ids: z.array(z.string().uuid()).min(1, 'At least one user required'),
    page_id: z.string().uuid().nullable().optional(),
    location: z.string().trim().max(1000).nullable().optional(),
});
export const uploadDmActivitySchema = z.object({
    upload_link: z.string().trim().min(1).max(2000),
});
export const scheduleDmActivitySchema = z.object({
    scheduled_date: z.string().datetime({ offset: true }),
});
export const listDmActivitiesQuerySchema = paginationQuerySchema.extend({
    status: dmActivityStatusSchema.optional(),
    page_id: z.string().uuid().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
    is_in_storage: z.preprocess(v => (v === 'true' ? true : v === 'false' ? false : v), z.boolean().optional()),
    q: z.string().optional(),
});
export const listDmCalendarQuerySchema = z.object({
    year: z.coerce.number().int().min(2020).max(2100),
    month: z.coerce.number().int().min(1).max(12),
    page_id: z.string().uuid().optional(),
});
export const createDmShootActivitySchema = z.object({
    title: z.string().trim().min(1).max(500),
    location: z.string().trim().max(1000).nullable().optional(),
    date_time: z.string().datetime({ offset: true }),
    who_shot_id: z.string().uuid().nullable().optional(),
    assigned_id: z.string().uuid().nullable().optional(),
});
export const updateDmShootActivitySchema = createDmShootActivitySchema.partial();
export const listDmShootActivitiesQuerySchema = paginationQuerySchema.extend({
    who_shot_id: z.string().uuid().optional(),
    assigned_id: z.string().uuid().optional(),
    date_from: z.string().date().optional(),
    date_to: z.string().date().optional(),
    q: z.string().optional(),
});
export const DM_ACCESS_TYPES = [
    'design_category_access',
    'designer_status_access',
    'designer_completed_access',
    'schedule_delete_permission',
    'activities_delete_permission',
    'complete_list_delete_permission',
    'shoot_team_members',
    'assign_list_members',
    'designer_reorder_access',
    'designer_delete_access',
];
export const dmAccessTypeSchema = z.enum(DM_ACCESS_TYPES);
export const addDmAccessUserSchema = z.object({
    user_id: z.string().uuid(),
});
// ── WATI miss-punch WhatsApp reminder ─────────────────────────────────────────
export const watiVariableMappingSchema = z
    .object({
    param_name: z.string().min(1).max(60),
    source_type: z.enum(['field', 'static']),
    source_field: z.enum(WATI_FIELD_SOURCES).nullable().optional(),
    static_value: z.string().max(1000).nullable().optional(),
})
    // Mirrors the wati_variable_mappings_source_present CHECK, so a bad mapping is a 400
    // at the boundary rather than a constraint violation surfacing as a 500.
    .refine((m) => (m.source_type === 'field' ? Boolean(m.source_field) : Boolean(m.static_value)), { message: 'A field mapping needs source_field; a static mapping needs static_value' });
/**
 * Evolution (self-hosted, Baileys) settings.
 *
 * `api_key` is write-only and optional-when-blank: GET /config never returns it, so a save
 * that leaves the field untouched must not blank the stored key. An empty string is
 * rejected rather than silently ignored, so "I cleared the box" is never mistaken for
 * "I left it alone".
 */
export const updateEvolutionConfigSchema = z.object({
    base_url: z.string().url('Base URL must be a valid URL').max(200).optional(),
    instance_name: z.string().min(1).max(60).optional(),
    api_key: z.string().min(1, 'API key cannot be blank').max(500).optional(),
    message_body: z.string().max(1000).nullable().optional(),
    send_delay_ms: z.number().int().min(0).max(30000).optional(),
    verify_numbers: z.boolean().optional(),
});
export const updateWatiConfigSchema = z.object({
    is_enabled: z.boolean().optional(),
    provider: z.enum(MESSAGE_PROVIDERS).optional(),
    send_daily: z.boolean().optional(),
    template_name: z.string().max(200).nullable().optional(),
    skip_sundays: z.boolean().optional(),
    run_time: z
        .string()
        .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Run time must be HH:MM (24-hour)')
        .optional(),
    /** Replaces the whole mapping set when present — templates own their variables. */
    mappings: z.array(watiVariableMappingSchema).max(50).optional(),
    evolution: updateEvolutionConfigSchema.optional(),
});
/**
 * Name for a new Evolution instance.
 *
 * The charset is not cosmetic: the name is interpolated into every upstream route path
 * (/instance/connect/{name}, /message/sendText/{name}), so anything that could carry a slash
 * or a path traversal is rejected here rather than encoded away downstream.
 */
export const EVOLUTION_INSTANCE_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
export const createEvolutionInstanceSchema = z.object({
    instance_name: z
        .string()
        .trim()
        .min(3, 'Give the instance a name of at least 3 characters')
        .max(60)
        .regex(EVOLUTION_INSTANCE_NAME_RE, 'Letters, numbers, hyphen and underscore only'),
});
export const watiTestSendSchema = z.object({
    phone: z.string().min(1, 'Phone is required').max(20),
});
// ── Face scan keystores (Rate API) ────────────────────────────────────────────
/** Rate API rule: caller-chosen and used as a URL path segment — no whitespace, "/" or control characters. */
export const faceScanKeystoreIdSchema = z
    .string()
    .trim()
    .min(1, 'Keystore ID is required')
    .max(200, 'Keystore ID must be 200 characters or fewer')
    .refine((id) => !/[\s/]/.test(id) && !Array.from(id).some((ch) => ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127), 'No spaces, "/" or control characters');
export const faceScanKeystoreUrlSchema = z
    .string()
    .trim()
    .min(1, 'URL is required')
    .max(2048, 'URL must be 2048 characters or fewer')
    .url('Enter a full URL')
    .refine((u) => /^https?:\/\//i.test(u), 'URL must start with http:// or https://');
export const createFaceScanKeystoreSchema = z.object({
    keystore_id: faceScanKeystoreIdSchema,
    url: faceScanKeystoreUrlSchema,
    label: z.string().trim().max(120, 'Label must be 120 characters or fewer').optional(),
    active: z.boolean().default(true),
});
/** Only the fields sent are changed on the Rate API; `label: null` clears it. */
export const updateFaceScanKeystoreSchema = z
    .object({
    url: faceScanKeystoreUrlSchema.optional(),
    label: z.string().trim().max(120, 'Label must be 120 characters or fewer').nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((v) => v.url !== undefined || v.label !== undefined || v.active !== undefined, {
    message: 'Nothing to update',
});
export const faceScanKeystoreListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['all', 'active', 'inactive']).default('all'),
});
// ── Reports · date-range endpoints ───────────────────────────────────────────
/**
 * Query shared by the newer report endpoints (`/reports/goals/*`,
 * `/reports/leave/employees`): a date range, an optional department, and
 * pagination.
 *
 * On the goal endpoints `department_id` is a request, not a guarantee — the
 * service pins non-admin callers to their own department regardless of what is
 * sent here.
 */
/** An omitted filter and a blank one mean the same thing to a query string. */
const blankToUndefined = (v) => (v === '' ? undefined : v);
export const reportsRangeQuerySchema = z.object({
    date_from: z.preprocess(blankToUndefined, z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional()),
    date_to: z.preprocess(blankToUndefined, z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional()),
    department_id: z.preprocess(blankToUndefined, z.string().uuid().optional()),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/** Employee-list reports (attendance, leave): a range plus a name/code search. */
export const reportsEmployeeListQuerySchema = reportsRangeQuerySchema.extend({
    q: z.preprocess(blankToUndefined, z.string().max(100).optional()),
});
// ── Salary pass key (own) ──────────────────────────────────────────────────────
/**
 * The 6-digit PIN an employee sets to unlock their own salary page.
 *
 * Kept as a string, never a number: a PIN is a digit sequence, and `Number('012345')`
 * would silently drop the leading zero and turn six digits into five.
 */
export const salaryPinSchema = z.object({
    pin: z.string().regex(/^\d{6}$/, 'PIN must be exactly 6 digits'),
});
// ── Desktop tracker (external tracker service, proxied) ──────────────────────
const trackerDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/** The tracker's userId is also its R2 folder name — used as a URL path segment. */
export const trackerUserIdSchema = z
    .string()
    .trim()
    .min(1, 'User ID is required')
    .max(200)
    .refine((id) => !/[\s/]/.test(id), 'No spaces or "/"');
/** The service rejects intervals under 10 s. `userId` is never editable — it names the R2 folder. */
export const trackerUserPatchSchema = z
    .object({
    name: z.string().trim().min(1, 'Name is required').max(120).optional(),
    email: z.string().trim().max(254).optional(),
    note: z.string().trim().max(500).nullable().optional(),
    enabled: z.boolean().optional(),
    captureAllMonitors: z.boolean().optional(),
    intervalSecs: z.number().int().min(10, 'Minimum interval is 10 seconds').max(86_400).optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to update' });
/** Per-day capture counts over an inclusive IST date range. */
export const trackerActivityQuerySchema = z.object({
    user_id: z.preprocess(blankToUndefined, trackerUserIdSchema.optional()),
    from: trackerDate,
    to: trackerDate,
});
/** Either one `date`, or an inclusive `from`–`to` IST range, or neither (everything). */
export const trackerImagesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(112).default(56),
    user_id: z.preprocess(blankToUndefined, trackerUserIdSchema.optional()),
    date: z.preprocess(blankToUndefined, trackerDate.optional()),
    from: z.preprocess(blankToUndefined, trackerDate.optional()),
    to: z.preprocess(blankToUndefined, trackerDate.optional()),
});
/** A dry run only counts rows whose R2 file is gone; the real run deletes those rows. */
export const trackerPruneSchema = z.object({
    user_id: trackerUserIdSchema.optional(),
    dry_run: z.boolean().default(true),
});
//# sourceMappingURL=zod-schemas.js.map